import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider } from 'next-themes';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import Layout from './components/Layout';
import PageTransition from './components/PageTransition';
import HomePage from './pages/HomePage';
import PlayerPage from './pages/PlayerPage';
import CalculatorPage from './pages/CalculatorPage';
import ClubPage from './pages/ClubPage';
import LeaguePage from './pages/LeaguePage';
import TournamentPage from './pages/TournamentPage';
import GamePage from './pages/GamePage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import NotFoundPage from './pages/NotFoundPage';
import CodesPage from './pages/CodesPage';
import CodeGeneratorPage from './pages/CodeGeneratorPage';
import WidgetPage from './pages/WidgetPage';
import WidgetConfiguratorPage from './pages/WidgetConfiguratorPage';
import Wrapped2025Page from './pages/Wrapped2025Page';
import { AuthProvider } from './contexts/AuthContext';
import { SystemMessageBanner } from './components/SystemMessageBanner';
import { WelcomeAnnouncement } from './components/WelcomeAnnouncement';
import './App.css';

// Component to handle redirects with parameters
const RedirectWithParams: React.FC<{ to: string }> = ({ to }) => {
  const params = useParams();
  let redirectPath = to;

  // Replace all parameter placeholders with actual values
  Object.entries(params).forEach(([key, value]) => {
    redirectPath = redirectPath.replace(`:${key}`, value as string);
  });

  return <Navigate to={redirectPath} replace />;
};


const theme = createTheme({
  primaryColor: 'blue',
  colors: {
    blue: [
      '#e6f2ff',
      '#baddff',
      '#8dc8ff',
      '#61b3ff',
      '#349eff',
      '#2563eb', // Main blue at index 5
      '#1C7AD6', // TTStats blue at index 6
      '#1862b3',
      '#144e8f',
      '#103a6b'
    ],
  },
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  defaultRadius: 'md',
});

function AppContent() {
  const location = useLocation();

  // Extract language from pathname (e.g., /fr/player/123 -> fr)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const langFromPath = pathParts[0];
  const validLangs = ['en', 'fr', 'de', 'it'];
  const currentLang = (validLangs.includes(langFromPath) ? langFromPath : 'en') as 'en' | 'fr' | 'de' | 'it';

  // Check if this is a widget or wrapped route (no layout needed)
  const isWidgetRoute = location.pathname.startsWith('/widget/');
  const isWrappedRoute = location.pathname.includes('/wrapped/2025/');

  if (isWidgetRoute) {
    return (
      <Routes location={location} key={location.pathname}>
        <Route path="/widget/:clubId" element={<WidgetPage />} />
      </Routes>
    );
  }

  if (isWrappedRoute) {
    return (
      <Routes location={location} key={location.pathname}>
        <Route path="/:lang/wrapped/2025/:url" element={<Wrapped2025Page />} />
        <Route path="/wrapped/2025/:url" element={<RedirectWithParams to="/en/wrapped/2025/:url" />} />
      </Routes>
    );
  }

  return (
    <>
      {/* Welcome Announcement */}
      <WelcomeAnnouncement currentLang={currentLang} />

      {/* System Messages Banner */}
      <SystemMessageBanner currentLang={currentLang} />

      <Layout>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
              {/* Language-specific routes */}
              <Route path="/:lang" element={<PageTransition><HomePage /></PageTransition>} />
              <Route path="/:lang/player/:url" element={<PageTransition><PlayerPage /></PageTransition>} />
              <Route path="/:lang/calculator" element={<PageTransition><CalculatorPage /></PageTransition>} />
              <Route path="/:lang/club/:id" element={<PageTransition><ClubPage /></PageTransition>} />
              <Route path="/:lang/club/:clubId/widget" element={<PageTransition><WidgetConfiguratorPage /></PageTransition>} />
              <Route path="/:lang/league/:id" element={<PageTransition><LeaguePage /></PageTransition>} />
              <Route path="/:lang/tournament/:id" element={<PageTransition><TournamentPage /></PageTransition>} />
              <Route path="/:lang/game/:id" element={<PageTransition><GamePage /></PageTransition>} />
              <Route path="/:lang/codes" element={<PageTransition><CodesPage /></PageTransition>} />
              <Route path="/:lang/codes/generate" element={<PageTransition><CodeGeneratorPage /></PageTransition>} />
              <Route path="/:lang/404" element={<PageTransition><NotFoundPage /></PageTransition>} />

              {/* Legal pages (no language prefix needed) */}
              <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
              <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
              <Route path="/404" element={<Navigate to="/en/404" replace />} />

              {/* Default routes (redirect to /en) */}
              <Route path="/" element={<Navigate to="/en" replace />} />
              <Route path="/player/:url" element={<RedirectWithParams to="/en/player/:url" />} />
              <Route path="/calculator" element={<Navigate to="/en/calculator" replace />} />
              <Route path="/club/:id" element={<RedirectWithParams to="/en/club/:id" />} />
              <Route path="/league/:id" element={<RedirectWithParams to="/en/league/:id" />} />
              <Route path="/tournament/:id" element={<RedirectWithParams to="/en/tournament/:id" />} />
              <Route path="/game/:id" element={<RedirectWithParams to="/en/game/:id" />} />
              <Route path="/codes" element={<Navigate to="/en/codes" replace />} />
              <Route path="/codes/generate" element={<Navigate to="/en/codes/generate" replace />} />

              {/* Catch-all 404 route - redirects to English 404 */}
              <Route path="*" element={<Navigate to="/en/404" replace />} />
            </Routes>
        </AnimatePresence>
      </Layout>
    </>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <MantineProvider theme={theme}>
        <Notifications position="bottom-center" />
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </MantineProvider>
    </ThemeProvider>
  );
}

export default App;
